<template>
    <q-dialog :model-value="show" @hide="close">
        <q-card class="split-dialog">
            <q-bar class="bg-primary text-white" color="white">
                <div>{{ $t('Split Downtime') }}</div>
                <q-space />
                <q-btn @click="$emit('close')" dense flat icon="close">
                    <q-tooltip>{{ $t('Close') }}</q-tooltip>
                </q-btn>
            </q-bar>

            <div class="q-pa-md">
                <!-- Warning banner for when the phase changes-->
                <q-banner
                    v-if="phaseHasChanged && !newPhaseId && !isLoading"
                    class="background-yellow border-yellow q-mb-md"
                    rounded
                    inline-actions
                >
                    <template v-slot:avatar>
                        <q-icon
                            name="fa-solid fa-triangle-exclamation"
                            color="warning"
                        ></q-icon>
                    </template>
                    {{
                        $t(
                            'The downtime you are trying to split is no longer the current phase. Visit the asset timeline to split the downtime.'
                        )
                    }}
                </q-banner>

                <!-- Warning banner for when split was successful but the cause change was not -->
                <q-banner
                    v-else-if="newPhaseId && hasJustificationError"
                    class="background-yellow border-yellow q-mb-md"
                    rounded
                    inline-actions
                >
                    <template v-slot:avatar>
                        <q-icon
                            name="fa-solid fa-triangle-exclamation"
                            color="warning"
                        ></q-icon>
                    </template>
                    {{
                        $t(
                            'Downtime split was successful but an error occurred while justifying the new downtime. Please try again.'
                        )
                    }}
                </q-banner>

                <!-- Phase Split Details -->
                <q-form ref="form" @submit="confirm">
                    <div v-if="!newPhaseId">
                        <b class="q-mr-xs">{{ $t('Current cause') }}: </b>
                        <span v-if="cause">{{ cause.name }}</span>
                        <span v-else>
                            <span v-if="isLoading || phaseHasChanged">
                                {{ $t('Missing cause') }}
                            </span>
                            <missing-cause-label
                                v-else
                                v-model="currentCauseSelect.selection"
                                :phaseId="phase.id"
                                :phaseUUID="phase.phaseUUID || ''"
                                @clearSelection="
                                    currentCauseSelect.selection = null
                                "
                                :baseURL="currentCauseSelect.url"
                                :queryParams="currentCauseSelect.queryParams"
                                :label="currentCauseSelect.label"
                                :dialogTitle="currentCauseSelect.dialogTitle"
                                :searchLabel="currentCauseSelect.searchLabel"
                                :isOutlined="true"
                            >
                                <template v-slot:item="data">
                                    <cause-card
                                        @select="
                                            selectCause(
                                                data.props.row,
                                                currentCauseSelect
                                            )
                                        "
                                        :name="data.props.row.name"
                                        :lossType="data.props.row.type_of_loss"
                                        :categoryName="
                                            data.props.row.category_name
                                        "
                                        :planned="data.props.row.planned"
                                        :description="
                                            data.props.row.description
                                        "
                                        :targetDowntime="
                                            data.props.row.target_down_time
                                        "
                                        :isSelected="
                                            currentCauseSelect.selection &&
                                            currentCauseSelect.selection
                                                .value === data.props.row.id
                                        "
                                        :isSelectable="true"
                                        :equipmentFailure="
                                            data.props.row.equipment_failure
                                        "
                                    ></cause-card>
                                </template> </missing-cause-label
                        ></span>
                    </div>
                    <div class="q-my-sm">
                        <b class="q-mr-xs">{{ $t('Current start') }}: </b>
                        {{ dateFormat(start, userLocale) }}
                    </div>

                    <q-separator class="q-my-md" />
                    <p class="q-my-sm">
                        <q-icon
                            v-if="newPhaseId && hasJustificationError"
                            name="fas fa-check-circle"
                            class="status__icon status__icon--positive q-mr-xs"
                        ></q-icon>
                        {{ $t('Select when to split the downtime') }}:
                    </p>
                    <date-time-picker
                        v-model="splitDateTime"
                        :label="$t('Next downtime starts at')"
                        :rules="formRules.datetime"
                        :disable="
                            isLoading ||
                            phaseHasChanged ||
                            (newPhaseId && hasJustificationError)
                        "
                        :dateRange="{
                            start,
                            end: currentDateTimeStr
                        }"
                        dense
                        class="q-my-sm"
                    />

                    <!-- New Phase Cause Details -->
                    <div class="q-py-sm">
                        <span class="q-mr-xs">
                            {{
                                $t('Select a cause for the new downtime')
                            }}:</span
                        >
                        <table-select
                            id="causes-select"
                            v-model="selectedNewCause"
                            @confirmSelection="
                                selectedNewCause = newCauseSelect.selection
                            "
                            :baseURL="newCauseSelect.url"
                            :queryParams="newCauseSelect.queryParams"
                            :label="$t('Cause')"
                            :dialogTitle="newCauseSelect.dialogTitle"
                            :searchLabel="newCauseSelect.searchLabel"
                            :dense="true"
                            class="col-lg-3 col-md-6 col-sm-6 col-xs-12 q-my-sm"
                            :disable="
                                isLoading || (phaseHasChanged && !newPhaseId)
                            "
                        >
                            <template v-slot:item="data">
                                <cause-card
                                    @select="
                                        selectCause(
                                            data.props.row,
                                            newCauseSelect
                                        )
                                    "
                                    :name="data.props.row.name"
                                    :lossType="data.props.row.type_of_loss"
                                    :categoryName="data.props.row.category_name"
                                    :planned="data.props.row.planned"
                                    :description="data.props.row.description"
                                    :targetDowntime="
                                        data.props.row.target_down_time
                                    "
                                    :isSelected="
                                        newCauseSelect.selection &&
                                        newCauseSelect.selection.value ===
                                            data.props.row.id
                                    "
                                    :isSelectable="true"
                                    :equipmentFailure="
                                        data.props.row.equipment_failure
                                    "
                                ></cause-card>
                            </template>
                        </table-select>
                    </div>

                    <div class="form__actions q-my-lg float-right">
                        <span
                            v-if="!phaseHasChanged || newPhaseId || isLoading"
                        >
                            <q-btn
                                @click="close"
                                :label="$t('Cancel')"
                                text-color="primary"
                                color="white"
                                outline
                                v-close-popup
                                :disable="isLoading"
                            />
                            <q-btn
                                type="submit"
                                :label="$t('Confirm')"
                                :loading="isLoading"
                                class="q-mx-sm"
                                color="primary"
                                :disable="isLoading || !hasSelectedNewCause"
                            />
                        </span>

                        <q-btn
                            v-else-if="
                                phaseHasChanged &&
                                !hasJustificationError &&
                                !newPhaseId
                            "
                            @click="close"
                            :disable="isLoading"
                            :label="$t('Close')"
                            text-color="primary"
                            color="white"
                            outline
                            v-close-popup
                        />
                    </div>
                </q-form>
            </div>
        </q-card>
    </q-dialog>
</template>

<script>
import CauseCard from '@components/CauseCard.vue';
import DateTimePicker from '@components/DateTimePicker.vue';
import MissingCauseLabel from '@components/MissingCauseLabel.vue';
import TableSelect from '@components/TableSelect.vue';
import { ASSET_STATUS_TO_STYLE } from '@models/assets.js';
import {
    LOCALE_DATETIME_FORMAT,
    getLocaleFormat
} from '@models/timeformats.js';
import AsyncManager from '@utils/asyncManager.js';
import { date } from 'quasar';
import { mapState, mapActions } from 'pinia';
import { useAuthStore } from '@stores/auth';
import { useCausesStore } from '@stores/causes.js';
import { useAssetsStore } from '@stores/assets';

export default {
    name: 'SplitDowntimeDialog',
    components: {
        DateTimePicker,
        TableSelect,
        CauseCard,
        MissingCauseLabel
    },
    emits: ['close'],
    props: {
        assetId: {
            type: String,
            required: true
        }
    },

    async created() {
        // Get current phase id
        this.phase = await this.getPhase();

        // initialize async manager instance
        this.asyncManager = new AsyncManager({});

        this.splitDateTime = this.currentDateTimeStr;
    },
    beforeUnmount() {
        // let the celery task continue executing but stop periodic status check
        try {
            this.asyncManager.stopPeriodicStatusCheck();
        } catch (error) {
            console.error(error.message);
        }
    },
    data() {
        return {
            show: true,

            // models
            ASSET_STATUS_TO_STYLE,
            LOCALE_DATETIME_FORMAT,

            // phase to be split
            phase: {
                id: null,
                phaseUUID: null,
                assetId: null,
                status: '',
                cause: null,
                currentProduct: null,
                start: null
            },
            newPhaseId: null, // if split is successful, this will be the id of the new phase
            hasJustificationError: false,
            // form details
            splitDateTime: null,
            formRules: {
                datetime: [
                    val =>
                        this.validateSplitTime(val) ||
                        this.$t(
                            'Select a datetime within the start and end of this phase.'
                        )
                ]
            },
            selectedNewCause: {
                value: null,
                label: ''
            },
            isLoading: false,

            // Cause select props
            currentCauseSelect: {
                url: `/assets/${this.assetId}/causes`,
                label: this.$t('Cause'),
                queryParams: { ordering: 'name' },
                dialogTitle: this.$t('Select a cause'),
                searchLabel: this.$t('Search cause'),
                selection: {}
            },
            newCauseSelect: {
                url: `/assets/${this.assetId}/causes`,
                label: this.$t('Cause'),
                queryParams: { ordering: 'name' },
                dialogTitle: this.$t('Select a cause'),
                searchLabel: this.$t('Search cause'),
                selection: {}
            }
        };
    },
    computed: {
        ...mapState(useAssetsStore, {
            getAssetPhase: 'getAssetPhase'
        }),
        ...mapState(useAuthStore, {
            userLocale: 'getUserLocale'
        }),
        ...mapState(useCausesStore, {
            getCause: 'getCause'
        }),
        /**
         * Payload in server format.
         * @returns {Object} payload
         */
        payload() {
            return { datetime: this.splitDateTime };
        },
        /**
         * Get the current date time
         * @returns {String} current date time
         */
        currentDateTimeStr() {
            return new Date().toISOString();
        },
        /**
         * Start of current phase
         * @returns {String} start of current phase in ISO format
         */
        start() {
            if (!this.currentPhase || !this.currentPhase.start) {
                return null;
            }

            const startDate = this.currentPhase.start;
            let formattedDate = date.formatDate(
                startDate,
                'YYYY-MM-DDTHH:mm:ss'
            );
            // The streaming app sends the date in a different format
            if (!formattedDate) {
                // extract date e.g. 2023-03-22 21:40:04.000000+0000 into 2023-03-22T21:40:04
                const dateObj = date.extractDate(
                    startDate,
                    'YYYY-MM-DD HH:mm:ss.SSSSSSZZ'
                );
                formattedDate = date.formatDate(dateObj, 'YYYY-MM-DDTHH:mm:ss');
            }

            return formattedDate;
        },
        /**
         * Cause of current phase
         * @returns {Object} cause of current phase
         */
        cause() {
            if (this.currentPhase) {
                return this.getCause(this.currentPhase.cause);
            } else {
                return null;
            }
        },
        /**
         * Get the current phase (this is not the same as the phase being split)
         * e.g. The user opens the dialog and the phase has already changed
         * currentPhase is the most recent phase retrieved from SignalR
         * this.phase is the phase that was retrieved when the dialog was opened
         * @returns {Object} current phase object
         */
        currentPhase() {
            return this.getAssetPhase(this.assetId);
        },
        /**
         * Has a selected cause for the new phase
         * @returns {Boolean}
         */
        hasSelectedNewCause() {
            return (
                this.selectedNewCause !== null &&
                this.selectedNewCause.value !== null
            );
        },
        /**
         * Boolean to check if phase has already changed
         * @returns {Boolean}
         */
        phaseHasChanged() {
            if (this.currentPhase && this.phase.id) {
                // we compare the start time and status of the phase because the phase details in the store
                // are limited. The SignalR message does not include the phase ID or phase UUID
                // TODO: Please refactor this when the phase details in the streaming message are updated
                // compare by phase UUID or phase ID
                return (
                    date.formatDate(this.phase.start, 'YYYY-MM-DDTHH:mm:ss') !==
                        this.start ||
                    this.phase.status !== this.currentPhase.status
                );
            }

            return false;
        }
    },
    methods: {
        ...mapActions(useAssetsStore, [
            'setAssetPhaseAction',
            'loadCurrentPhasesAction'
        ]),
        /**
         * Format a date to local date format
         * @param {String} datetime
         * @param {String} locale
         * @return {String} datetime in given locale.
         */
        dateFormat(val, locale) {
            return getLocaleFormat(val, locale);
        },
        /**
         * Send request to server to split.
         *
         */
        async confirm() {
            this.isLoading = true;

            if (!this.newPhaseId) {
                const url = `assetphases/${this.phase.id}/split/`;

                const payload = {
                    date: this.splitDateTime,
                    asynchronous: true
                };
                const errorMessage = this.$t(
                    'Failed to split phase. Please try again later.'
                );
                try {
                    const result = await this.asyncManager.asyncLoad(
                        url,
                        payload
                    );
                    if ('error' in result[0]) {
                        // error during split execution
                        const error = result[0]['error'];
                        if (error === 'unprocessed_phases') {
                            this.$notifyError(
                                this.$t(
                                    'Data for this phase is still processing. Please try again later.'
                                )
                            );
                        } else if (error === 'latest_phase') {
                            this.$notifyError(
                                this.$t(
                                    'Failed to split phase. The selected phase is no longer the latest phase.'
                                )
                            );
                        } else if (error === 'split_date') {
                            this.$notifyError(
                                this.$t('Split date is invalid.')
                            );
                        } else if (error == 'uneditable_phase') {
                            this.$notifyError(
                                this.$t(
                                    "Phases that have finished more than {editablePeriod} days ago can't be edited.",
                                    {
                                        editablePeriod: parseInt(
                                            process.env
                                                .EDITABLE_PHASE_DURATION_DAYS ||
                                                5 // 5 days
                                        )
                                    }
                                )
                            );
                        } else {
                            // unknown error
                            this.$notifyError(
                                this.$t(
                                    'Failed to split phase. Please try again later.'
                                ),
                                error
                            );
                        }
                    } else {
                        // notify user
                        this.$notifySuccess(this.$t('Phase has been split.'));

                        // iterate through response data results and get the more recent phase
                        // this is the new phase that was created and will be justified with the new cause
                        const newPhase = result[0]['results'].reduce(
                            (prev, current) => {
                                return new Date(prev['start']) >
                                    new Date(current['start'])
                                    ? prev
                                    : current;
                            }
                        );

                        // set new phase in store
                        this.setAssetPhaseAction({
                            id: newPhase['id'],
                            phaseUUID: newPhase['uuid'],
                            assetId: newPhase['asset'],
                            status: newPhase['phase'],
                            cause: newPhase['cause'],
                            currentProduct: newPhase['product'],
                            start: newPhase['start'],
                            lockedTransitionToRunning:
                                newPhase['locked_transition_to_running'] ??
                                false
                        });

                        this.newPhaseId = newPhase['id'];
                    }
                } catch (error) {
                    if (
                        error &&
                        typeof error == 'object' &&
                        'uneditable_phase' in error
                    ) {
                        this.$notifyError(
                            this.$t(
                                "Phases that have finished more than {editablePeriod} days ago can't be edited.",
                                {
                                    editablePeriod: parseInt(
                                        process.env
                                            .EDITABLE_PHASE_DURATION_DAYS || 5 // 5 days
                                    )
                                }
                            )
                        );
                    } else {
                        this.$notifyError(errorMessage, error);
                    }
                }
            }

            // if a new phase was created successfully, update the cause
            if (this.newPhaseId) {
                this.justifyDowntime(this.newPhaseId);
                this.close();
            }

            this.isLoading = false;
        },
        /**
         * Add a cause to the new phase after the split.
         * @param {Number} phaseId to be justified
         */
        justifyDowntime(phaseId) {
            this.isLoading = true;

            const payload = {
                cause: this.selectedNewCause.value
            };

            const errorMessage = this.$t(
                'Failed to update cause at this time. Please try again later.'
            );
            this.$axios
                .patch(`events/${phaseId}/`, payload)
                .then(() => {
                    this.$notifySuccess(
                        this.$t('Cause has been successfully updated')
                    );
                    this.newPhaseId = null;
                })
                .catch(error => {
                    this.hasJustificationError = true;
                    this.$notifyError(errorMessage, error);
                });

            // Enable submit button
            this.isLoading = false;
        },
        /**
         * Get the current phase of the asset with full details from the API as the store
         * does not always contain some phase details including the phase ID (which is required for justifying downtime)
         */
        async getPhase() {
            if (!this.currentPhase?.id) {
                const errorMessage = this.$t(
                    'Failed to save current phase details. Please try again later.'
                );
                // Get the current phase of the asset from the API since the phase ID if not available in the store
                try {
                    await this.loadCurrentPhasesAction({
                        assetId: this.assetId
                    });
                    return this.getAssetPhase(this.assetId);
                } catch (error) {
                    this.$notifyError(errorMessage, error);
                }
            }

            return this.currentPhase;
        },
        /**
         * Close the dialog.
         */
        close() {
            this.$emit('close');
        },
        /**
         * Check that the time to split the phase is within the phase start and end.
         * @param {Date} datetime
         * @return {Boolean}
         */
        validateSplitTime(datetime) {
            if (datetime) {
                // use cross browser datetime format
                let end_date = new Date();
                let formattedDatetime = datetime.replace(/-/g, '/');
                return (
                    new Date(this.start) < new Date(formattedDatetime) &&
                    new Date(formattedDatetime) < end_date
                );
            }
            return false;
        },
        /**
         * Select/ deselect cause in table-select component
         * @param {Object} row - Event from table row.
         * @param {Object} props - Table selection props (causeTableSelect, MissingCauseLabel)
         */
        selectCause(row, props) {
            if (props.selection && props.selection.value === row.id) {
                props.selection = null;
            } else {
                props.selection = {
                    label: row.name,
                    value: row.id
                };
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.split-dialog {
    width: 100%;
}

.status__icon {
    font-size: 20px;
}

.status__icon--positive {
    color: $positive;
}
</style>
